import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getEventById } from '../data/vaultEvents';
import './VaultDetail.css';

/**
 * VaultDetail - Component to display detailed information about a vault event
 * Dynamically loads content based on the event ID in the URL
 */
const VaultDetail = () => {
  // Get the event ID from the URL parameters
  const { eventId } = useParams();
  
  // Component state
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);
  
  // Fetch event data when component mounts or URL changes
  useEffect(() => {
    setLoading(true);
    
    try {
      // Retrieve event data using the ID from the URL
      const eventData = getEventById(eventId);
      
      if (eventData) {
        setEvent(eventData);
        setError(null);
      } else {
        setError('Event not found');
      }
    } catch (err) {
      setError('Error loading event details');
      console.error('Error loading event:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);
  
  // Handle play button click to open video
  const handlePlayClick = () => {
    setVideoOpen(true);
  };
  
  // Helper function to safely load images
  const safelyLoadImage = (path) => {
    try {
      return require(path);
    } catch (error) {
      console.error(`Failed to load image: ${path}`, error);
      return null;
    }
  };
  
  // For the hero section:
  const heroImage = () => {
    try {
      return require(`../assets/thumbnails/${event.videoThumbnail}`);
    } catch (error) {
      console.error(`Failed to load thumbnail: ${event.videoThumbnail}`, error);
      return null;
    }
  };
  
  // Display loading spinner while fetching data
  if (loading) {
    return (
      <div className="page-container">
        <div className="vault-detail-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }
  
  // Display error message if event was not found
  if (error || !event) {
    return (
      <div className="page-container">
        <div className="vault-detail-container">
          <div className="error-container">
            <p className="error-message">{error || 'Event not found'}</p>
            <Link to="/the-vault" className="error-button">
              Back to Vault
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="page-container">
      <div className="vault-detail-container">
        {/* Back button */}
        <Link to="/the-vault" className="back-button">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 19L5 12L12 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Vault
        </Link>

        {/* Page Title */}
        <h1 className="vault-main-title">{event.title}</h1>
        <h2 className="vault-subtitle">{event.featuredArtists.join(', ')}</h2>
        
        {/* Write Up Section */}
        <section className="write-up-section">
          {/* <h3 className="section-title">WRITE UP</h3> */}
          <p className="event-description">{event.description}</p>
        </section>

        {/* Photos Section */}
        {event.photos && event.photos.length > 0 && (
          <section className="photos-section">
            {/* <h3 className="section-title">PHOTOS</h3> */}
            <div className="photos-grid">
              {event.photos.map((photo, index) => (
                <div className="photo-item" key={index}>
                  {(() => {
                    try {
                      const photoImage = require(`../assets/${photo}`);
                      return (
                        <img 
                          src={photoImage} 
                          alt={`Event photo ${index + 1}`} 
                          className="event-photo"
                        />
                      );
                    } catch (error) {
                      console.error(`Failed to load photo: ${photo}`, error);
                      return null;
                    }
                  })()}
                </div>
              ))}
            </div>
          </section>
        )}
        
        {/* Recap Video Section */}
        {event.videoId && (
          <section className="recap-video-section">
            <h3 className="section-title">RECAP VIDEO</h3>
            <div className="video-container">
              <iframe 
                src={`https://www.youtube.com/embed/${event.videoId}`}
                title={`${event.title} Recap`}
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              ></iframe>
            </div>
          </section>
        )}

        {/* Posters Section - Only show if no photos and no video */}
        {(!event.photos || event.photos.length === 0) && !event.videoId && (
          <section className="posters-section">
            <div className="posters-grid">
              {(() => {
                try {
                  const posterImage = require(`../assets/posters/${event.posterImage}`);
                  return (
                    <img 
                      src={posterImage} 
                      alt={`${event.title} Poster`} 
                      className="poster-image" 
                    />
                  );
                } catch (error) {
                  console.error(`Failed to load poster: ${event.posterImage}`, error);
                  return (
                    <div className="poster-placeholder">
                      <p>Poster not available</p>
                    </div>
                  );
                }
              })()}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default VaultDetail; 